/**
 * Cliente HTTP de la Práctica de Escenarios para `apps/speak`.
 *
 * Cubre el catálogo curado (`GET /scenarios`), la generación a medida
 * (`POST /scenarios/generate`) y "Mis escenarios" (`GET /my-scenarios`,
 * `DELETE /my-scenarios/:id`). Los tipos se redefinen acá en lugar de
 * importar de `@orborix/module-languages` para que speak quede autocontenida
 * (mismo criterio que `sessionSummaryApi.ts`): el cliente sólo necesita el
 * shape UI-facing, no el contrato interno del módulo. Si en el futuro hace
 * falta compartirlo, promoverlo a `packages/shared-types` con zod schema.
 *
 * Las requests pasan por `apiFetch` (apiClient): en web la cookie httpOnly de
 * sesión viaja sola same-origin (`/api` vía el proxy de Vite en dev); en nativo
 * agrega la base absoluta + `Authorization: Bearer` + `X-Orborix-Native`.
 */

import type { ScenarioProgress } from '@orborix/shared-types';

import { createTypedApiClient } from '@orborix/auth-client';

export type { ScenarioProgress } from '@orborix/shared-types';

const BASE = '/api/modules/english';

/** Intensidades del módulo english (1:1 con `ConversationPrefs['intensity']`). */
export type ScenarioIntensity = 'cozy' | 'natural' | 'push' | 'stretch' | 'native';

/** Objetivo tal como lo ve el cliente: sin el `criterion` (instrucción del juez). */
export interface PublicScenarioObjective {
  id: string;
  label: string;
  kind: 'primary' | 'bonus';
  /** Piso de intensidad para estar activo (gating por dificultad). Ausente = siempre. */
  minIntensity?: ScenarioIntensity;
}

/** Escenario curado tal como lo expone el catálogo. */
export interface PublicScenario {
  id: string;
  category: string;
  title: string;
  blurb: string;
  emoji: string;
  durationMin: number;
  suggestedIntensity: string;
  /** Presente sólo en curados con misión. */
  objectives?: ReadonlyArray<PublicScenarioObjective>;
}

export interface ScenarioCategoryMeta {
  id: string;
  label: string;
  emoji: string;
}

export interface PublicScenarioGroup {
  category: string;
  label: string;
  emoji: string;
  scenarios: ReadonlyArray<PublicScenario>;
}

export interface ScenarioCatalogResponse {
  categories: ReadonlyArray<ScenarioCategoryMeta>;
  groups: ReadonlyArray<PublicScenarioGroup>;
}

/**
 * Escenario GENERADO a medida (sin misión). Subset de un escenario curado sin
 * `objectives`/`curveball`: roleplay inmersivo. Se guarda al pasar al briefing
 * ("Mis escenas") y viaja al runtime como hint inline al abrir la sesión.
 *
 * OJO: el runtime valida el inline con un schema ESTRICTO — este shape tiene
 * que viajar tal cual, sin campos extra (un `createdAt` de más lo convierte en
 * "escenario malformado" y la sesión cae a charla libre).
 */
export interface GeneratedScenario {
  id: string;
  category: string;
  title: string;
  blurb: string;
  emoji: string;
  durationMin: number;
  suggestedIntensity: ScenarioIntensity;
  coachRole: string;
  userRole: string;
}

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'ScenariosApiError',
  // Los fetchers aceptan `signal` pero ningún caller se lo pasa, así que el
  // `AbortError` no es alcanzable; si algún día lo fuera, el criterio para
  // pasar a `'rethrow'` está en `docs/architecture/09-speak.md`.
  abortError: 'wrap',
});

/** Error con código crudo del backend; el caller decide el copy user-facing. */
export const ScenariosApiError = client.ApiError;
export type ScenariosApiError = InstanceType<typeof client.ApiError>;

const { get, send } = client;

/** Catálogo curado de escenarios (categorías + grupos con sus escenarios). */
export function fetchScenarioCatalog(signal?: AbortSignal): Promise<ScenarioCatalogResponse> {
  return get<ScenarioCatalogResponse>('/scenarios', signal ? { signal } : {});
}

/**
 * Progreso del user por escenario (badges del hub + CTA rejugar). Endpoint
 * per-user, separado del catálogo (cacheable). Devuelve sólo los escenarios con
 * al menos una sesión jugada; el caller indexa por `scenarioId`.
 */
export function fetchScenarioProgress(signal?: AbortSignal): Promise<ReadonlyArray<ScenarioProgress>> {
  return get<{ progress: ReadonlyArray<ScenarioProgress> }>(
    '/scenarios/progress',
    signal ? { signal } : {},
  ).then((r) => r.progress);
}

/**
 * Genera un escenario a medida desde una situación en texto libre. Devuelve el
 * `GeneratedScenario` (sin misión). El caller lo pasa como `inlineScenario` al
 * abrir la sesión de conversación.
 */
export function generateScenario(situation: string): Promise<{ scenario: GeneratedScenario }> {
  return send<{ scenario: GeneratedScenario }>('/scenarios/generate', {
    method: 'POST',
    body: JSON.stringify({ situation }),
  });
}

/**
 * Guarda el generado que PASÓ al briefing (sin botón "Guardar": confirmar la
 * escena es guardarla; los descartados con «reintentar» no se guardan).
 * Idempotente por id. 400 `limit_reached` (con `max`) al tope.
 */
export function saveMyScenario(scenario: GeneratedScenario, situation: string): Promise<void> {
  return send<{ ok: true }>('/my-scenarios', {
    method: 'POST',
    body: JSON.stringify({ scenario, situation }),
  }).then(() => undefined);
}

/**
 * Mis escenas: los generados del user que pasaron al briefing, más recientes
 * primero. Vienen COMPLETOS: el hub los manda tal cual como `inlineScenario`
 * al abrir la sesión, igual que uno recién generado.
 */
export function fetchMyScenarios(signal?: AbortSignal): Promise<ReadonlyArray<GeneratedScenario>> {
  return get<{ scenarios: ReadonlyArray<GeneratedScenario> }>(
    '/my-scenarios',
    signal ? { signal } : {},
  ).then((r) => r.scenarios);
}

/** Borra un escenario propio. 404 si no existe o no es del user. */
export function deleteMyScenario(id: string): Promise<void> {
  return send<{ ok: true }>(`/my-scenarios/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  }).then(() => undefined);
}
